import type { ApiResponse } from '../types/api'
import { api } from './api-client'
import { trackEvent } from './gtm'

interface GameUrlParams {
  brandId: string
  gameDomain: string
  token: string
}

// 1 = real money, 0 = play-for-fun, 2 = guest mode (BTI only)
export type GameLaunchMode = 0 | 1 | 2

interface GetGameLauncherUrlArgs {
  gameCode: string
  mode: GameLaunchMode
  isDesktop: boolean
  username: string
}

interface GetGameLauncherUrlLegacyArgs {
  id: string
  mode: GameLaunchMode
  isDesktop: boolean
  username: string
}

interface GetGameLauncherUrlLegacyBtiArgs {
  isDesktop: boolean
  username: string
}

export interface GameLauncherUrlResult {
  status: number
  url?: string
  error?: string
}

// Low-level pieces — pages go through `useGameLauncher.launch` or `getGameLauncherUrlWithFallback`.

// Fetches a player-level session; null on a bad status. Split from URL building so the token can be cached.
export const fetchGameUrlParams = async (
  username: string,
): Promise<GameUrlParams | null> => {
  try {
    const reqBody: Record<string, string> = { type: 'get_game_url_params' }
    if (!username) {
      reqBody.username = 'guest'
      reqBody.password = 'guest'
    }

    const response = await api.post<ApiResponse<GameUrlParams | string>>(
      '/request.php',
      reqBody,
    )
    const { info, status } = response.data

    if (status !== 1 || typeof info === 'string') return null

    return info
  } catch (e) {
    return null
  }
}

// True when the JWT is expired; malformed or `exp`-less tokens count as expired.
export const isGameTokenExpired = (token: string): boolean => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return true

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    )
    const { exp } = JSON.parse(atob(padded)) as { exp?: number }
    if (!exp) return true

    return Date.now() >= exp * 1000
  } catch {
    return true
  }
}

// Builds the game launcher URL on the frontend from previously-fetched params.
export const buildGameLauncherUrl = (
  { brandId, gameDomain, token }: GameUrlParams,
  { gameCode, mode, isDesktop, username }: GetGameLauncherUrlArgs,
): string => {
  const params = new URLSearchParams({
    gameCode,
    token,
    platform: isDesktop ? 'web' : 'mobile',
    language: 'es',
    playerId: username || 'guest',
    brandId,
    mode: String(mode),
    backUrl: `${window.location.origin}/game-back`,
    cashierUrl: `${window.location.origin}/?continue=deposit`,
  })

  return `${gameDomain}/launcher?${params.toString()}`
}

// Fetches a session via `get_game_url_params` and builds the launcher URL.
export const getGameLauncherUrl = async (
  args: GetGameLauncherUrlArgs,
): Promise<GameLauncherUrlResult> => {
  const params = await fetchGameUrlParams(args.username)
  if (!params) return { status: 0 }

  return { status: 1, url: buildGameLauncherUrl(params, args) }
}

// Legacy flow: the backend returns the launcher URL directly in `info`.
export const getGameLauncherUrlLegacy = async ({
  id,
  mode,
  isDesktop,
  username,
}: GetGameLauncherUrlLegacyArgs): Promise<GameLauncherUrlResult> => {
  const reqBody: Record<string, string | number> = {
    // ⚠ Endpoint follows `mode`, not login state — see docs/memory/game-launcher-url-flows.md
    type:
      mode === 0 || !username
        ? 'try_play_game_websocket2'
        : 'play_game_websocket2',
    id,
    platform: isDesktop ? 0 : 1,
    // Legacy convention: new-flow `mode: 0` becomes `mode: 2` in this body.
    ...(mode === 0 && { mode: 2 }),
  }

  const response = await api.post<ApiResponse<string>>('/request.php', reqBody)
  const { info, status } = response.data

  if (status !== 1 || typeof info !== 'string') {
    return { status, error: typeof info === 'string' ? info : undefined }
  }
  return { status, url: info }
}

// The sports lobby's game id — the launcher request and the wallet transfer must agree.
export const SPORTS_LOBBY_GAME_ID = 1886

// Sports-lobby legacy flow; `id` is the only param read — see docs/memory/sports-entry-funsports-lobby.md
export const getGameLauncherUrlLegacyBti = async ({
  isDesktop,
  username,
}: GetGameLauncherUrlLegacyBtiArgs): Promise<GameLauncherUrlResult> => {
  const reqBody: Record<string, string | number> = {
    type: 'play_game_websocket2',
    id: String(SPORTS_LOBBY_GAME_ID),
    platform: isDesktop ? 0 : 1,
  }
  if (!username) {
    reqBody.mode = 3
    reqBody.username = 'guest'
    reqBody.password = 'guest'
  }

  const response = await api.post<ApiResponse<string>>('/request.php', reqBody)
  const { info, status } = response.data

  if (status !== 1 || typeof info !== 'string') {
    return { status, error: typeof info === 'string' ? info : undefined }
  }
  return { status, url: info }
}

export interface GetGameLauncherUrlWithFallbackArgs
  extends GetGameLauncherUrlArgs {
  // Legacy id, derived from the route segment; ignored when `legacyVariant` is 'bti'.
  id: string
  // Which legacy request shape to fall back to.
  legacyVariant?: 'casino' | 'bti'
}

// Page-level entry; `useGameLauncher` wraps it to add session caching and pre-fetching.

// New flow first, legacy on failure (emits `game_launcher_fallback`); always resolves. Pass `params` to skip re-fetching the session.
export const getGameLauncherUrlWithFallback = async (
  args: GetGameLauncherUrlWithFallbackArgs,
  params?: GameUrlParams | null,
): Promise<GameLauncherUrlResult> => {
  const fallback = async (
    reason: 'bad_status' | 'thrown',
  ): Promise<GameLauncherUrlResult> => {
    trackEvent('game_launcher_fallback', {
      event_category: 'casino',
      event_action: 'fallback',
      event_label: reason,
      game_code: args.gameCode,
    })
    try {
      if (args.legacyVariant === 'bti') {
        return await getGameLauncherUrlLegacyBti({
          isDesktop: args.isDesktop,
          username: args.username,
        })
      }
      return await getGameLauncherUrlLegacy({
        id: args.id,
        mode: args.mode,
        isDesktop: args.isDesktop,
        username: args.username,
      })
    } catch {
      return {
        status: 0,
        error: 'No se pudo cargar el juego. Inténtalo de nuevo.',
      }
    }
  }

  // ⚠ Logged-in play-for-fun must go through legacy, and emits no fallback event — see docs/memory/game-launcher-url-flows.md
  if (args.mode === 0 && args.username) {
    try {
      return await getGameLauncherUrlLegacy({
        id: args.id,
        mode: args.mode,
        isDesktop: args.isDesktop,
        username: args.username,
      })
    } catch {
      return {
        status: 0,
        error: 'No se pudo cargar el juego. Inténtalo de nuevo.',
      }
    }
  }

  if (params) {
    try {
      return { status: 1, url: buildGameLauncherUrl(params, args) }
    } catch {
      return fallback('thrown')
    }
  }

  try {
    const result = await getGameLauncherUrl(args)
    if (result.status === 1 && result.url) return result
    return fallback('bad_status')
  } catch {
    return fallback('thrown')
  }
}
